/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { Form } from '../../molecules/PatternForm';
import StatusToast from 'components/molecules/StatusToast/StatusToast';
import { createPattern, getAllChutes, getAllRules } from '../../../services/templateService';
import { Chute } from 'interfaces/templates';
import { useNavigate } from 'react-router-dom';

interface Group {
  group_id?: number;
  chutes: number[];
}

export default function PatternCreate() {
  const navigate = useNavigate();

  const [groups, setGroups] = useState<Group[]>([{ chutes: [] }]);
  const [ruleOptions, setRuleOptions] = useState<{ value: string; label: string }[]>([]);
  const [baseChutes, setBaseChutes] = useState<Chute[]>([]);
  const [patternName, setPatternName] = useState('');
  const [toast, setToast] = useState<{ title: string; type: 'success' | 'error' | 'loading'; visible: boolean }>({
    title: '',
    type: 'success',
    visible: false,
  });

  const showToast = (title: string, type: 'success' | 'error' | 'loading') => {
    setToast({ title, type, visible: true });
    setTimeout(() => {
      setToast(prev => ({ ...prev, visible: false }));
    }, 3000);
  };

  const handleAddRule = () => {
    setGroups(prev => [...prev, { chutes: [] }]);
  };

  const handleChutesSelected = (ruleIndex: number, selectedChuteNumbers: number[]) => {
    setGroups(prev => {
      const updated = [...prev];
      const selectedChuteIds = selectedChuteNumbers
        .map(num => baseChutes.find(c => c.chute === num)?.id)
        .filter((id): id is number => id !== undefined);
      updated[ruleIndex].chutes = selectedChuteIds;
      return updated;
    });
  };

  const handleDeleteRule = (indexToDelete: number) => {
    const updatedGroups = groups.filter((_, idx) => idx !== indexToDelete);
    setGroups(updatedGroups);
  };

  const handleUpdateGroup = (ruleIndex: number, group_id: number, chutes: number[]) => {
    setGroups(prev => {
      const updated = [...prev];
      updated[ruleIndex] = { group_id, chutes };
      return updated;
    });
  };

  const transformGroupsToPayload = (): { group_id: number; chutes: number[] }[] => {
    return groups
      .filter((g): g is { group_id: number; chutes: number[] } => typeof g.group_id === 'number')
      .map(group => ({
        group_id: group.group_id,
        chutes: group.chutes
          .map(id => baseChutes.find(c => c.id === id)?.chute)
          .filter((chute): chute is number => chute !== undefined),
      }));
  };

  const handleCreate = async () => {
    if (!patternName.trim()) {
      return showToast('El nombre de la plantilla es requerido', 'error');
    }

    // if (groups.length === 0 || groups.some(g => g.chutes.length === 0)) {
    if (groups.length === 0 ) {
      return showToast('Todas las reglas deben tener al menos un chute asignado', 'error');
    }

    try {
      const formData = {
        name: patternName.trim(),
        groups: transformGroupsToPayload(),
      };

      const data = await createPattern(formData);

      showToast('Plantilla creada con éxito', 'success');
      navigate(`/templates/show/${data.data.id}`);
    } catch (error: any) {
      showToast(error?.message || 'Error al crear la plantilla', 'error');
    }
  };

  const buttonValidate = () => {
    // return (!patternName.trim() || groups.length === 0 || groups.some(g => g.chutes.length === 0))
    return (!patternName.trim() || groups.length === 0 )
  }

  const renderRuleItems = () =>
    groups.map((group, index) => {
      const defaultChutes = group.chutes
        .map(chute => {
          const match = baseChutes.find(c => c.chute === chute);
          return match ? { chute: match.chute, id: match.id } : null;
        })
        .filter((c): c is { chute: number; id: number } => c !== null);

      const disabledRuleIds = groups
        .map(g => g.group_id)
        .filter((gid): gid is number => typeof gid === 'number')
        .filter((gid, i) => i !== index);

      const assignedChutes = groups.flatMap((g, i) => (i === index ? [] : g.chutes));

      return (
        <React.Fragment key={group.group_id ?? `new-${index}`}>
          <Form.RuleItem
            chutes={baseChutes}
            ruleOptions={ruleOptions}
            onDelete={() => handleDeleteRule(index)}
            ruleIndex={index}
            onChutesSelected={handleChutesSelected}
            selectedChutes={assignedChutes}
            onGroupUpdate={handleUpdateGroup}
            selectedRuleId={group.group_id?.toString()}
            defaultChutes={defaultChutes}
            disabledRuleIds={disabledRuleIds}
          />
          <hr />
        </React.Fragment>
      );
    });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [rulesResponse, chutesResponse] = await Promise.all([
          getAllRules(),
          getAllChutes('sorting'),
        ]);

        const options = rulesResponse?.groups?.map((group: { id: number; name: string }) => ({
          value: group.id.toString(),
          label: group.name,
        })) ?? [];
        setRuleOptions(options);

        const chutes = chutesResponse.data?.default_chutes || [];
        const transformedChutes: Chute[] = chutes.map((item: any) => ({
          id: item.id,
          chute: item.number,
          type: 'available',
        }));
        setBaseChutes(transformedChutes);
      } catch {
        setRuleOptions([]);
        setBaseChutes([]);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="h-screen w-full mx-5 flex flex-col">
      <div className="flex-1 min-h-0 overflow-auto">
        <Form
          onAddRule={handleAddRule}
          onCreateTemplate={handleCreate}
          onNameChange={val => setPatternName(val)}
          templateName={patternName}
          buttonDisabled={buttonValidate()}
        >
          {renderRuleItems()}
        </Form>
      </div>

      {toast.visible && <StatusToast title={toast.title} type={toast.type} />}
    </div>
  );
}
